"use client"

import { useState, useEffect } from "react"
import { Save, Gamepad2, Clock, Timer } from "lucide-react"
import { getSchool, updateGameConfig } from "@/hooks/api/schoolApi"
import config from "../../../../../config"

const GAMES = [
  { id: "equation_scape", name: "EquationScape", subject: "Mathematics" },
  { id: "fix_it_challenge", name: "Fix It Challenge", subject: "Technology" },
  { id: "naira_tycoon", name: "Naira Tycoon", subject: "Economics" },
  { id: "word_smith", name: "WordSmith", subject: "English" },
  { id: "compound_connector", name: "Compound Connector", subject: "Chemistry" },
  { id: "biology_kingdom", name: "Biology Kingdom", subject: "Biology" },
  { id: "budget_blitz", name: "Budget Blitz", subject: "Financial Literacy" },
  { id: "capital_scramble", name: "Capital Scramble", subject: "Geography" },
  { id: "civic_spin", name: "Civic Spin", subject: "Civic Education" },
  { id: "match_game", name: "Match Game", subject: "English / Vocabulary" },
]

interface GameConfig {
  enabledGames: string[]
  dailyTimeWindow: { enabled: boolean; startTime: string; endTime: string }
  dailyLimitMinutes: number
}

const DEFAULT_CONFIG: GameConfig = {
  enabledGames: [],
  dailyTimeWindow: { enabled: false, startTime: "08:00", endTime: "18:00" },
  dailyLimitMinutes: 0,
}

export function GameControlsTab() {
  const [cfg, setCfg] = useState<GameConfig>(DEFAULT_CONFIG)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (config.appMode === "prototype") {
      setCfg(DEFAULT_CONFIG)
      setLoading(false)
      return
    }
    getSchool()
      .then((s) => {
        const gc = (s as any).gameConfig
        if (gc) setCfg({ ...DEFAULT_CONFIG, ...gc, dailyTimeWindow: { ...DEFAULT_CONFIG.dailyTimeWindow, ...gc.dailyTimeWindow } })
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const allEnabled = cfg.enabledGames.length === 0

  const toggleGame = (gameId: string) => {
    setCfg((prev) => {
      const current = prev.enabledGames.length === 0
        ? GAMES.map((g) => g.id)
        : [...prev.enabledGames]

      const idx = current.indexOf(gameId)
      const next = idx >= 0 ? current.filter((id) => id !== gameId) : [...current, gameId]

      // empty = all enabled
      return { ...prev, enabledGames: next.length === GAMES.length ? [] : next }
    })
  }

  const toggleAll = () => {
    setCfg((prev) => ({ ...prev, enabledGames: [] }))
  }

  const isEnabled = (gameId: string) => allEnabled || cfg.enabledGames.includes(gameId)

  const handleSave = async () => {
    setSaving(true)
    try {
      if (config.appMode !== "prototype") await updateGameConfig(cfg)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="text-sm text-slate-400 py-8 text-center">Loading game settings…</div>
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-bold text-slate-900">Game Controls</h3>
        <p className="text-sm text-slate-500 mt-0.5">Control which games students can access and when.</p>
      </div>

      {/* Per-game toggles */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Available Games</label>
          <button onClick={toggleAll} className="text-xs text-indigo-600 hover:underline font-semibold">
            Enable All
          </button>
        </div>
        <div className="grid gap-2">
          {GAMES.map((game) => {
            const enabled = isEnabled(game.id)
            return (
              <div key={game.id} className={`flex items-center gap-3 rounded-xl border p-3 transition-colors ${enabled ? "border-indigo-100 bg-indigo-50/40" : "border-slate-200 bg-slate-50"}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${enabled ? "bg-indigo-100" : "bg-slate-100"}`}>
                  <Gamepad2 size={14} className={enabled ? "text-indigo-600" : "text-slate-400"} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${enabled ? "text-slate-900" : "text-slate-500"}`}>{game.name}</p>
                  <p className="text-xs text-slate-400">{game.subject}</p>
                </div>
                <button
                  onClick={() => toggleGame(game.id)}
                  className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${enabled ? "bg-indigo-500" : "bg-slate-300"}`}
                >
                  <span className={`inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-4" : "translate-x-0.5"}`} />
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {/* Time window */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock size={16} className="text-slate-500" />
            <div>
              <p className="text-sm font-semibold text-slate-900">Daily Time Window</p>
              <p className="text-xs text-slate-400">Restrict games to specific hours (school time, Africa/Lagos)</p>
            </div>
          </div>
          <button
            onClick={() => setCfg((c) => ({ ...c, dailyTimeWindow: { ...c.dailyTimeWindow, enabled: !c.dailyTimeWindow.enabled } }))}
            className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${cfg.dailyTimeWindow.enabled ? "bg-indigo-500" : "bg-slate-300"}`}
          >
            <span className={`inline-block h-4 w-4 mt-0.5 rounded-full bg-white shadow transition-transform ${cfg.dailyTimeWindow.enabled ? "translate-x-4" : "translate-x-0.5"}`} />
          </button>
        </div>

        {cfg.dailyTimeWindow.enabled && (
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div>
              <label className="text-xs text-slate-500 font-semibold">Start Time</label>
              <input
                type="time"
                value={cfg.dailyTimeWindow.startTime}
                onChange={(e) => setCfg((c) => ({ ...c, dailyTimeWindow: { ...c.dailyTimeWindow, startTime: e.target.value } }))}
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 font-semibold">End Time</label>
              <input
                type="time"
                value={cfg.dailyTimeWindow.endTime}
                onChange={(e) => setCfg((c) => ({ ...c, dailyTimeWindow: { ...c.dailyTimeWindow, endTime: e.target.value } }))}
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
          </div>
        )}
      </div>

      {/* Daily limit */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Timer size={16} className="text-slate-500" />
          <div>
            <p className="text-sm font-semibold text-slate-900">Daily Time Limit</p>
            <p className="text-xs text-slate-400">Max total game time per student per day (0 = no limit)</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min={0}
            max={480}
            value={cfg.dailyLimitMinutes}
            onChange={(e) => setCfg((c) => ({ ...c, dailyLimitMinutes: Math.max(0, Number(e.target.value)) }))}
            className="w-24 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
          <span className="text-sm text-slate-500">minutes</span>
          {cfg.dailyLimitMinutes > 0 && (
            <span className="text-xs text-indigo-600 font-semibold">
              ({Math.floor(cfg.dailyLimitMinutes / 60)}h {cfg.dailyLimitMinutes % 60}m per student per day)
            </span>
          )}
        </div>
      </div>

      <div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-xl"
        >
          <Save size={14} />
          {saving ? "Saving…" : saved ? "Saved!" : "Save Game Settings"}
        </button>
      </div>
    </div>
  )
}
